import React, { useContext } from "react";
import { alpha, makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import ButtonBase from "@material-ui/core/ButtonBase";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";

import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

export const ADVISOR_COLORS = [
	"#7E16FF",
	"#0F7B8A",
	"#C2410C",
	"#2563EB",
	"#BE185D",
	"#4D7C0F",
];

const STATUS_COLORS = {
	CONNECTED: "#2E9E52",
	qrcode: "#D98200",
	OPENING: "#D98200",
	PAIRING: "#D98200",
};

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
		height: "100%",
		overflowY: "auto",
		...theme.scrollbarStyles,
	},
	heading: {
		margin: theme.spacing(0.5, 0.5, 0.5),
		fontSize: "0.75rem",
		fontWeight: 600,
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		color: theme.palette.text.secondary,
	},
	card: {
		display: "flex",
		flexDirection: "column",
		alignItems: "stretch",
		gap: theme.spacing(1.5),
		padding: theme.spacing(1.75),
		borderRadius: 12,
		textAlign: "left",
		backgroundColor: theme.palette.background.paper,
		border: `1.5px solid ${theme.palette.divider}`,
		"&:hover": {
			borderColor: alpha(theme.palette.primary.main, 0.5),
		},
	},
	cardSelected: {
		backgroundColor: alpha(theme.palette.primary.main, 0.06),
		borderColor: theme.palette.primary.main,
		"&:hover": {
			borderColor: theme.palette.primary.main,
		},
	},
	identity: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.25),
		minWidth: 0,
	},
	avatar: {
		flex: "none",
		width: 40,
		height: 40,
		borderRadius: "50%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#fff",
		fontWeight: 600,
		fontSize: "0.875rem",
	},
	name: {
		fontWeight: 600,
		fontSize: "0.9375rem",
		lineHeight: 1.3,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	status: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		fontSize: "0.75rem",
		color: theme.palette.text.secondary,
	},
	statusDot: {
		width: 8,
		height: 8,
		borderRadius: "50%",
	},
	stats: {
		display: "grid",
		gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
		gap: 6,
	},
	stat: {
		display: "flex",
		flexDirection: "column",
		gap: 2,
		padding: theme.spacing(1),
		borderRadius: 8,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
	},
	statWaiting: {
		backgroundColor: theme.palette.type === "dark" ? alpha("#FFAF04", 0.16) : "#FFF4E5",
		borderColor: theme.palette.type === "dark" ? alpha("#FFAF04", 0.3) : "#FBE1BC",
		"& $statValue, & $statLabel": {
			color: theme.palette.type === "dark" ? "#FFC25C" : "#8A4B00",
		},
	},
	statHot: {
		backgroundColor: theme.palette.type === "dark" ? alpha("#F74848", 0.16) : "#FFECEC",
		borderColor: theme.palette.type === "dark" ? alpha("#F74848", 0.3) : "#F8CFCF",
		"& $statValue, & $statLabel": {
			color: theme.palette.type === "dark" ? "#FF8A8A" : "#A3161B",
		},
	},
	statValue: {
		fontSize: "1.125rem",
		fontWeight: 700,
		lineHeight: 1.2,
		color: theme.palette.text.primary,
	},
	statLabel: {
		fontSize: "0.6875rem",
		color: theme.palette.text.secondary,
	},
}));

const initialsOf = name =>
	(name || "?")
		.split(" ")
		.filter(Boolean)
		.map(part => part[0])
		.slice(0, 2)
		.join("")
		.toUpperCase();

const AdvisorCard = ({ name, color, initials, statusText, statusColor, counts, selected, onClick }) => {
	const classes = useStyles();

	return (
		<ButtonBase
			component="button"
			focusRipple
			onClick={onClick}
			aria-pressed={selected}
			className={`${classes.card} ${selected ? classes.cardSelected : ""}`}
		>
			<span className={classes.identity}>
				<span className={classes.avatar} style={{ backgroundColor: color }}>
					{initials}
				</span>
				<span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
					<span className={classes.name}>{name}</span>
					{statusText && (
						<span className={classes.status}>
							<span className={classes.statusDot} style={{ backgroundColor: statusColor }} />
							{statusText}
						</span>
					)}
				</span>
			</span>
			<span className={classes.stats}>
				<span className={classes.stat}>
					<span className={classes.statValue}>{counts.active}</span>
					<span className={classes.statLabel}>{i18n.t("advisors.stats.active")}</span>
				</span>
				<span className={`${classes.stat} ${classes.statWaiting}`}>
					<span className={classes.statValue}>{counts.waiting}</span>
					<span className={classes.statLabel}>{i18n.t("advisors.stats.waiting")}</span>
				</span>
				<span className={`${classes.stat} ${classes.statHot}`}>
					<span className={classes.statValue}>{counts.hot}</span>
					<span className={classes.statLabel}>HOT</span>
				</span>
			</span>
		</ButtonBase>
	);
};

const AdvisorsPanel = ({ selectedWhatsappId, onSelect, summary = [] }) => {
	const classes = useStyles();
	const { whatsApps } = useContext(WhatsAppsContext);

	const countsFor = whatsappId => {
		const row = summary.find(s => s.whatsappId === whatsappId);
		return row || { active: 0, waiting: 0, hot: 0 };
	};

	const totals = summary.reduce(
		(acc, s) => ({
			active: acc.active + s.active,
			waiting: acc.waiting + s.waiting,
			hot: acc.hot + s.hot,
		}),
		{ active: 0, waiting: 0, hot: 0 }
	);

	return (
		<nav className={classes.root} aria-label={i18n.t("advisors.title")}>
			<Typography component="h2" className={classes.heading}>
				{i18n.t("advisors.title")}
			</Typography>
			<AdvisorCard
				name={i18n.t("advisors.all")}
				color="#4A4656"
				initials={<PeopleAltOutlinedIcon fontSize="small" />}
				counts={totals}
				selected={!selectedWhatsappId}
				onClick={() => onSelect(null)}
			/>
			{whatsApps.map((whatsApp, index) => (
				<AdvisorCard
					key={whatsApp.id}
					name={whatsApp.name}
					color={ADVISOR_COLORS[index % ADVISOR_COLORS.length]}
					initials={initialsOf(whatsApp.name)}
					statusText={i18n.t(`advisors.status.${whatsApp.status}`, {
						defaultValue: i18n.t("advisors.status.DISCONNECTED"),
					})}
					statusColor={STATUS_COLORS[whatsApp.status] || "#9A96A6"}
					counts={countsFor(whatsApp.id)}
					selected={selectedWhatsappId === whatsApp.id}
					onClick={() => onSelect(whatsApp.id)}
				/>
			))}
		</nav>
	);
};

export default AdvisorsPanel;
